import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, RefreshCw, AlertTriangle, CheckCircle, Trash2, FileText, Download, Sparkles, Wrench, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DatabaseManagement = () => {
  const navigate = useNavigate();
  const [sequenceStatus, setSequenceStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Collection documentation state
  const [docStatus, setDocStatus] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Bin level fix state
  const [binFixResult, setBinFixResult] = useState(null);
  const [isFixingBins, setIsFixingBins] = useState(false);
  const [showBinFixConfirm, setShowBinFixConfirm] = useState(false);

  useEffect(() => {
    fetchSequenceStatus();
    fetchDocumentationStatus();
  }, []);

  const fetchDocumentationStatus = async () => {
    try {
      const response = await apiClient.get('/admin/collection-documentation/status');
      setDocStatus(response.data);
    } catch (error) {
      console.error('Error fetching documentation status:', error);
    }
  };

  const handleDownloadDocumentation = async () => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get('/admin/collection-documentation/download', {
        responseType: 'blob',
        params: { auto_refresh: true }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `collection_documentation_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Documentation downloaded successfully!');
      // Refresh status after download
      fetchDocumentationStatus();
    } catch (error) {
      toast.error('Failed to download documentation');
      console.error('Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRegenerateDocumentation = async () => {
    setIsRegenerating(true);
    try {
      await apiClient.post('/admin/collection-documentation/regenerate');
      toast.success('Documentation regenerated successfully!');
      fetchDocumentationStatus();
    } catch (error) {
      toast.error('Failed to regenerate documentation');
      console.error('Error:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const fetchSequenceStatus = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/sequence-status');
      setSequenceStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch sequence status');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'RESET_ALL_DATA') {
      toast.error('Please type RESET_ALL_DATA to confirm');
      return;
    }

    setIsResetting(true);
    try {
      const response = await apiClient.post(`/admin/reset-database?confirmation=${confirmText}`);
      toast.success('Database reset successful!');
      setSequenceStatus({
        current_fiscal_year: response.data.new_fiscal_year,
        document_sequence: { code_type: 'DOC', current_number: 1, fiscal_year: response.data.new_fiscal_year },
        bundle_sequence: { code_type: 'BUN', current_number: 1, fiscal_year: response.data.new_fiscal_year },
        next_doc_code: response.data.next_doc_code,
        next_bun_code: response.data.next_bun_code
      });
      setShowResetConfirm(false);
      setConfirmText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset database');
    } finally {
      setIsResetting(false);
    }
  };

  // Bin Level ID Fix handlers
  const handleBinLevelPreview = async () => {
    setIsFixingBins(true);
    setBinFixResult(null);
    try {
      const response = await apiClient.post('/admin/fix-bin-level-ids?dry_run=true');
      setBinFixResult(response.data);
      if (response.data.summary.updated === 0) {
        toast.success('All bins already have correct level references!');
      } else {
        toast.info(`Preview: ${response.data.summary.updated} bins need to be updated`);
        setShowBinFixConfirm(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview bin fixes');
    } finally {
      setIsFixingBins(false);
    }
  };

  const handleBinLevelFix = async () => {
    setIsFixingBins(true);
    try {
      const response = await apiClient.post('/admin/fix-bin-level-ids?dry_run=false');
      setBinFixResult(response.data);
      toast.success(`Successfully updated ${response.data.summary.updated} bins!`);
      setShowBinFixConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix bin level IDs');
    } finally {
      setIsFixingBins(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Database Management</h1>
              <p className="text-sm text-[#E8DCC8]">Manage sequences and reset data</p>
            </div>
          </div>
          <Button onClick={fetchSequenceStatus} variant="secondary" className="h-10" disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Collection Documentation Card */}
        <Card className="mb-6 border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <FileText className="w-5 h-5" />
              Collection Documentation
            </CardTitle>
            <CardDescription>Download PDF with all MongoDB collection schemas and sample data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {docStatus && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-[#F5F1E8] rounded-lg">
                    <p className="text-sm text-[#5A5F6E] mb-1">Total Collections</p>
                    <p className="text-xl font-bold text-[#1A1F2E]">{docStatus.current_collection_count}</p>
                  </div>
                  
                  {docStatus.pdf_exists && docStatus.pdf_info && (
                    <>
                      <div className="p-3 bg-[#F5F1E8] rounded-lg">
                        <p className="text-sm text-[#5A5F6E] mb-1">PDF Size</p>
                        <p className="text-xl font-bold text-[#1A1F2E]">{docStatus.pdf_info.size_mb} MB</p>
                      </div>
                      <div className="p-3 bg-[#F5F1E8] rounded-lg">
                        <p className="text-sm text-[#5A5F6E] mb-1">Last Updated</p>
                        <p className="text-sm font-medium text-[#1A1F2E]">
                          {new Date(docStatus.pdf_info.last_modified).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {docStatus?.needs_refresh && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800">
                    {docStatus.new_collections?.length > 0 
                      ? `${docStatus.new_collections.length} new collection(s) detected: ${docStatus.new_collections.slice(0, 3).join(', ')}${docStatus.new_collections.length > 3 ? '...' : ''}`
                      : 'PDF needs to be generated'
                    }
                  </span>
                  <Badge variant="outline" className="ml-auto text-amber-700 border-amber-300">Refresh Needed</Badge>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleDownloadDocumentation}
                  disabled={isDownloading}
                  className="bg-[#2D5F4F] hover:bg-[#234a3d]"
                  data-testid="download-collection-docs-btn"
                >
                  {isDownloading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {docStatus?.needs_refresh ? 'Generating & Downloading...' : 'Downloading...'}
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleRegenerateDocumentation}
                  disabled={isRegenerating}
                  data-testid="regenerate-collection-docs-btn"
                >
                  {isRegenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Force Regenerate
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-[#5A5F6E]">
                The PDF automatically refreshes when new collections are detected during download.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sequence Status Card */}
        <Card className="mb-6 border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Database className="w-5 h-5" />
              Code Sequence Status
            </CardTitle>
            <CardDescription>Current fiscal year and sequence counters</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-[#2D5F4F]" />
              </div>
            ) : sequenceStatus ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-[#F5F1E8] rounded-lg">
                  <p className="text-sm text-[#5A5F6E] mb-1">Current Fiscal Year</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{sequenceStatus.current_fiscal_year}</p>
                  <p className="text-xs text-[#5A5F6E] mt-1">Apr {20}{sequenceStatus.current_fiscal_year?.split('-')[0]} - Mar {20}{sequenceStatus.current_fiscal_year?.split('-')[1]}</p>
                </div>
                
                <div className="p-4 bg-[#F5F1E8] rounded-lg">
                  <p className="text-sm text-[#5A5F6E] mb-1">Document Barcode Counter</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{sequenceStatus.document_sequence?.current_number || 0}</p>
                  <p className="text-xs text-[#5A5F6E] mt-1">Next: {sequenceStatus.next_doc_code}</p>
                </div>
                
                <div className="p-4 bg-[#F5F1E8] rounded-lg">
                  <p className="text-sm text-[#5A5F6E] mb-1">Bundle QR Code Counter</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{sequenceStatus.bundle_sequence?.current_number || 0}</p>
                  <p className="text-xs text-[#5A5F6E] mt-1">Next: {sequenceStatus.next_bun_code}</p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Auto-Reset Enabled
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Counters automatically reset to 1 on April 1st each year
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-center text-[#5A5F6E]">Failed to load sequence status</p>
            )}
          </CardContent>
        </Card>

        {/* Fix Bin Level IDs Card */}
        <Card className="mb-6 border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Layers className="w-5 h-5" />
              Fix Bin Level References
            </CardTitle>
            <CardDescription>
              Corrects bin level_id references when bins are not appearing under their correct levels in the hierarchy.
              This fixes issues where bins show under wrong levels due to data migration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {binFixResult && (
                <div className={`p-4 rounded-lg border ${binFixResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {binFixResult.dry_run ? (
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    ) : (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                    <span className={`font-medium ${binFixResult.dry_run ? 'text-amber-800' : 'text-green-800'}`}>
                      {binFixResult.message}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-500">Total Bins</p>
                      <p className="font-bold">{binFixResult.summary.total_bins}</p>
                    </div>
                    <div className="p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-500">Total Levels</p>
                      <p className="font-bold">{binFixResult.summary.total_levels}</p>
                    </div>
                    <div className="p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-500">{binFixResult.dry_run ? 'To Update' : 'Updated'}</p>
                      <p className="font-bold text-amber-600">{binFixResult.summary.updated}</p>
                    </div>
                    <div className="p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-500">Skipped</p>
                      <p className="font-bold text-gray-600">{binFixResult.summary.skipped}</p>
                    </div>
                  </div>
                  
                  {binFixResult.updated_details?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">
                        {binFixResult.dry_run ? 'Bins to be updated:' : 'Updated bins:'}
                      </p>
                      <div className="max-h-40 overflow-y-auto bg-white/50 rounded p-2 text-xs space-y-1">
                        {binFixResult.updated_details.map((detail, idx) => (
                          <div key={idx} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                            <span className="font-mono">{detail.bin_code}</span>
                            <span className="text-gray-500">→ {detail.level_code}</span>
                          </div>
                        ))}
                        {binFixResult.summary.updated > binFixResult.updated_details.length && (
                          <p className="text-gray-500 italic pt-1">
                            ... and {binFixResult.summary.updated - binFixResult.updated_details.length} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {binFixResult.error_details?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-red-700 mb-2">Errors ({binFixResult.summary.errors}):</p>
                      <div className="max-h-32 overflow-y-auto bg-red-50 rounded p-2 text-xs space-y-1">
                        {binFixResult.error_details.map((detail, idx) => (
                          <div key={idx} className="text-red-600">
                            {detail.bin_code}: Level "{detail.expected_level}" not found
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {!showBinFixConfirm ? (
                <Button
                  onClick={handleBinLevelPreview}
                  disabled={isFixingBins}
                  className="bg-[#2D5F4F] hover:bg-[#234a3d]"
                  data-testid="preview-bin-fix-btn"
                >
                  {isFixingBins ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Preview Bin Level Fixes
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    onClick={handleBinLevelFix}
                    disabled={isFixingBins}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="apply-bin-fix-btn"
                  >
                    {isFixingBins ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Apply Fixes ({binFixResult?.summary?.updated || 0} bins)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBinFixConfirm(false);
                      setBinFixResult(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-[#5A5F6E]">
                This fixes bins that aren't showing under the correct level in the Location Setup hierarchy.
                Run "Preview" first to see what will be changed.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Reset Database Card */}
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Reset Database
            </CardTitle>
            <CardDescription className="text-red-600">
              This will delete all inward entries and reset code counters. Users, roles, and sites will be preserved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showResetConfirm ? (
              <Button 
                variant="destructive" 
                onClick={() => setShowResetConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Reset All Operational Data
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    This action cannot be undone. The following will be deleted:
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                    <li>All inward entries</li>
                    <li>All documentation records</li>
                    <li>Code sequence counters (reset to 1)</li>
                  </ul>
                </div>
                
                <div>
                  <Label className="text-red-700">Type RESET_ALL_DATA to confirm:</Label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESET_ALL_DATA"
                    className="mt-1 border-red-300 focus:border-red-500"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="destructive"
                    onClick={handleReset}
                    disabled={isResetting || confirmText !== 'RESET_ALL_DATA'}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isResetting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Confirm Reset
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowResetConfirm(false);
                      setConfirmText('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DatabaseManagement;
